import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, PageBody, Spinner } from '@wundo/ui';
import { Check } from 'lucide-react';
import { todayInBrussels } from '@wundo/shared/todayInBrussels.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { CohortFilter } from '@/components/pickers/CohortFilter.tsx';
import { DashboardMetrics } from '@/components/dashboard/DashboardMetrics.tsx';
import { fetchDashboard } from '@/lib/dashboard.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { useCohortInOrganisation } from '@/lib/useCohortInOrganisation.ts';

export interface DashboardSearch {
  /** Which cohort the figures are about. Absent is the whole organisation. */
  cohort: string | undefined;
}

export const Route = createFileRoute('/_app/dashboard/')({
  staticData: { orgScope: 'required' },
  validateSearch: (search: Record<string, unknown>): DashboardSearch => ({
    cohort:
      typeof search.cohort === 'string' && search.cohort !== ''
        ? search.cohort
        : undefined,
  }),
  component: DashboardRoute,
});

function DashboardRoute() {
  const { cohort } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <DashboardPage
      cohort={cohort}
      /* The cohort lives in the URL, so a dashboard can be linked to as it is read. */
      onSelectCohort={(next) =>
        void navigate({ to: '/dashboard', search: { cohort: next } })
      }
    />
  );
}

export interface DashboardPageProps {
  cohort: string | undefined;
  onSelectCohort: (cohort: string | undefined) => void;
}

export function DashboardPage({ cohort: asked, onSelectCohort }: DashboardPageProps) {
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id;
  const { cohort, settled } = useCohortInOrganisation(asked, organisationId);

  const dashboard = useQuery({
    queryKey: ['dashboard', cohort ?? null, organisationId ?? null],
    queryFn: () => fetchDashboard({ cohortId: cohort, organisationId }),
    enabled: settled,
    throwOnError: true,
  });

  const meta = dashboard.data?.meta;
  const accessEndsOn = meta?.cohort?.pilot_timeline?.access_ends_on;
  const trackEnded = accessEndsOn !== undefined && todayInBrussels() >= accessEndsOn;

  return (
    <>
      <PageHeader
        title='Dashboard'
        badge={
          trackEnded && (
            <Badge tone='neutral'>
              <Check className='size-3' aria-hidden='true' />
              Track ended
            </Badge>
          )
        }
        filters={
          organisationId && (
            <CohortFilter
              value={cohort}
              onChange={onSelectCohort}
              organisationId={organisationId}
            />
          )
        }
      />

      <PageBody>
        {dashboard.data ? (
          <DashboardMetrics data={dashboard.data.data} cohortId={cohort} />
        ) : (
          <p className='flex items-center gap-2 text-sm text-ink-muted'>
            <Spinner />
            Loading dashboard…
          </p>
        )}
      </PageBody>
    </>
  );
}
